/**
 * The four states every screen owes: loading, empty, error and partial.
 *
 * AN EMPTY STATE THAT SAYS NOTHING IS A BUG, NOT A BLANK. Every `EmptyState` below takes a
 * headline AND an explanation, because "no rows" is never the whole truth: on this surface it is
 * either "you have not published anything yet", "the range you picked has nothing in it", or "the
 * filter you set matches nothing" — three different sentences with three different next actions.
 *
 * THE FOURTH STATE IS THE ONE PEOPLE FORGET. A partial read is a page that has SOME of its answer:
 * a table already drawn while its next page loads, a dashboard whose tiles are half `ok` and half
 * `not_reported_yet`, a payments view that loaded while the payout method did not. Those are
 * rendered as content plus a quiet line, never as a spinner over the whole screen — losing what is
 * already on screen to show a spinner is a regression the reader can see.
 *
 * WHY THESE LIVE HERE AND NOT IN web/ui. docs/ui/03-components.md's inventory puts EmptyState,
 * ErrorState, LoadingState, MetricTile, Money and StatusPill in the shared package, and that is
 * where they belong. They are here because six agents are writing this repo at the same time over
 * disjoint trees, and two of them creating `web/ui/src/components/EmptyState/` is a silent
 * last-writer-wins collision. Promoting these is a file move and an import change; nothing about
 * them is cabinet-specific.
 */
import { Alert, Button, Center, Group, Loader, Skeleton, Stack, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import { describeProblem } from '../api';

export interface LoadingStateProps {
  /** What is being read, in the author's words. Announced, so it is never a bare spinner. */
  label: string;
  /** Rows of skeleton to stand in for a table. Zero draws a centred spinner instead. */
  rows?: number;
}

export function LoadingState({ label, rows = 0 }: LoadingStateProps) {
  if (rows === 0) {
    return (
      <Center py="xl">
        <Group gap="sm">
          <Loader size="sm" />
          {/* The label is real text, not an aria-label: a sighted reader on a slow line needs it too. */}
          <Text size="sm" c="dimmed">
            {label}
          </Text>
        </Group>
      </Center>
    );
  }

  return (
    // `role="status"` is what lets the label be read at all: an `aria-label` on a plain `div` names
    // nothing, and axe refuses it (`aria-prohibited-attr`) - found scanning the read view, 2026-10-05.
    <Stack gap="xs" role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_unused, index) => (
        // A skeleton must be readable at frame zero: global.css neutralises every animation under
        // `prefers-reduced-motion`, so nothing may exist only while something is moving.
        <Skeleton key={index} height={40} radius="sm" />
      ))}
    </Stack>
  );
}

export interface ErrorStateProps {
  title?: string;
  error: unknown;
  onRetry?: () => void;
}

export function ErrorState({ title, error, onRetry }: ErrorStateProps) {
  const { t } = useTranslation('cabinet');
  return (
    <Alert
      variant="light"
      title={title ?? t('states.errorTitle')}
      role="alert"
      /* The palette carries `--mx-danger`; Mantine's own `color="red"` would reach for a colour
         that is not in this brand's token set. Set the tokens explicitly instead. */
      styles={{
        root: {
          backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
          border: '1px solid oklch(var(--mx-danger) / 0.45)',
        },
        title: { color: 'oklch(var(--mx-danger))' },
      }}
    >
      <Stack gap="sm" align="flex-start">
        {/* `describeProblem` reads `error_code`, which the contract says is the only thing a client
            may branch on — never `title`, never `detail`. */}
        <Text size="sm">{describeProblem(error)}</Text>
        {onRetry === undefined ? null : (
          <Button size="xs" variant="default" onClick={onRetry}>
            {t('states.tryAgain')}
          </Button>
        )}
      </Stack>
    </Alert>
  );
}

export interface EmptyStateProps {
  /** One line. What the reader is looking at. */
  headline: string;
  /** Why it is empty and what would change it. REQUIRED — a blank panel explains nothing. */
  explanation: string;
  action?: ReactNode;
}

export function EmptyState({ headline, explanation, action }: EmptyStateProps) {
  return (
    <Stack
      gap="xs"
      align="center"
      py="xl"
      px="md"
      style={{
        border: '1px dashed oklch(var(--mx-border-dash))',
        borderRadius: 'var(--mantine-radius-lg)',
      }}
    >
      <Title order={3} size="h5">
        {headline}
      </Title>
      <Text size="sm" c="dimmed" ta="center" maw={480}>
        {explanation}
      </Text>
      {action}
    </Stack>
  );
}

/**
 * The quiet line that makes a partial state legible: content stays, and a small note says a read
 * is in flight. Used under a table that already has rows, and beside a figure being refreshed.
 */
export function RefreshingNote({ label }: { label: string }) {
  return (
    <Group gap="xs" aria-live="polite">
      <Loader size="xs" />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
}

/**
 * A section that loaded when its neighbours did not.
 *
 * Used where one screen makes several independent reads — /payments reads the view AND the payout
 * method — so one failure degrades one panel instead of blanking the page.
 */
export function PartialFailureNote({ what, error }: { what: string; error: unknown }) {
  const { t } = useTranslation('cabinet');
  return (
    <Alert
      variant="light"
      role="status"
      title={t('states.partialFailure', { what })}
      styles={{
        root: {
          backgroundColor: 'oklch(var(--mx-warning) / 0.10)',
          border: '1px solid oklch(var(--mx-warning) / 0.45)',
        },
        // The warning INK, web/ui's per-scheme alias: light `warning` read 3.99 on this tint over
        // the page, the ink 6.04; dark is `warning` itself (web/authors/test/tinted-inks.spec.tsx).
        title: { color: 'oklch(var(--mx-warning-ink))' },
      }}
    >
      <Text size="sm">{describeProblem(error)}</Text>
    </Alert>
  );
}
